<template>
  <CModal
    title="プロフィール基本情報編集"
    :active="isVisible"
    @close="$emit('close')"
    width="640px"
  >
    <section>
      <CField label="プロフィール画像">
        <div class="c-editor_userIcon">
          <CUserIconV2
            :file-path="attributes.user.resizableProfileIcon"
            :user-name="attributes.user.name"
            :size="120"
            :width="240"
            :height="240"
          />
          <div class="c-editor_link">
            <a :href="$coconala.url('/mypage/user/info')">プロフィール画像を変更する</a>
          </div>
        </div>
      </CField>

      <CField label="名義の区分（個人/法人・個人事業主）">
        <div class="c-editor_businessFormat">
          <span>{{ corporateFgText }}</span>
          <span>
            <a
              :href="$coconala.url('/mypage/user_information')"
              class="c-editor_businessFormatLink"
            >名義の区分を登録/変更する</a>
          </span>
        </div>
      </CField>

      <CField
        label="職業"
        required
        :error-message="getErrorMessage('attributes.user.profile.occupation', '職業')"
      >
        <div class="c-editor_occupation">
          <CInput
            placeholder="例）ママさんデザイナー"
            :maxlength="MAX_OCCUPATION"
            v-model="attributes.user.profile.occupation"
          />
        </div>
      </CField>

      <CField label="年齢層">
        <div class="c-editor_age">
          <CCheckbox v-model="attributes.user.profile.isDisplayAge">
            年齢層（{{ attributes.user.age }}）を公開する
          </CCheckbox>
        </div>
      </CField>

      <CField label="性別">
        <div class="c-editor_sex">
          {{ sexText }}
        </div>
      </CField>

      <CField
        :class="{ 'c-field_location': isOtherLocation }"
        label="居住エリア"
      >
        <div class="c-editor_location">
          <CSelect
            placeholder="選択してください"
            v-model="attributes.user.profile.prefectureId"
          >
            <option :value="null">-</option>
            <option
              v-for="(prefecture, index) in prefectures"
              :key="index"
              :value="prefecture.id"
            >
              {{ prefecture.name }}
            </option>
          </CSelect>
        </div>
      </CField>
      <CField
        v-if="isOtherLocation"
        :error-message="getErrorMessage('attributes.user.profile.locationText', '居住エリア')"
      >
        <div class="c-editor_locationText">
          <CInput
            placeholder="居住エリアを入力してください"
            :maxlength="MAX_LOCATION_TEXT"
            v-model="attributes.user.profile.locationText"
          />
        </div>
      </CField>

      <CField
        label="業務領域"
        required
      >
        <div class="c-editor_job">
          <p class="c-editor_jobNotes">
            最大10個まで得意なカテゴリを登録できます。<br>
            登録すると「出品者を探す」のカテゴリ検索結果に表示されます。
          </p>
          <FormError :message="getErrorMessage('attributes.userJobs', '業務領域')" />
          <JobsPicker
            :jobs="jobs"
            :user-jobs="attributes.userJobs"
            v-model="attributes.userJobs"
          />
        </div>
      </CField>

      <CField
        label="テーマカラー（背景色）"
        v-if="false"
      >
        <div class="c-editor_theme">
          TODO: v2
        </div>
      </CField>
    </section>
    <div slot="footer">
      <div class="c-formButtons">
        <CButton @click="$emit('close')">キャンセル</CButton>
        <CButton
          color="primary"
          @click="update"
          :is-loading="isLoading"
        >更新する</CButton>
      </div>
    </div>
  </CModal>
</template>
<script>
import FormError from '~/components/atoms/FormError'

// local components
import JobsPicker from './components/JobsPicker'

// vuex
import { mapState, mapActions } from 'vuex'

// mixins
import formMixin from '~/mixins/form-mixin'

// others
import {
  required,
  maxArrayLength,
  maxLength,
  emoji,
  char4Byte
} from '~/assets/javascripts/validators'

const MAX_OCCUPATION = 20
const MAX_LOCATION_TEXT = 10
const MAX_USER_JOBS = 10

export default {
  name: 'EditorBasicInfo',
  mixins: [formMixin],
  components: {
    FormError,
    JobsPicker
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    user: {
      type: Object
    }
  },
  data() {
    const user = this.$util.clone(this.user)
    return {
      attributes: {
        user,
        userJobs: user.userJobsList.map(j => j.jobId)
      },
      isLoading: false,
      MAX_OCCUPATION,
      MAX_LOCATION_TEXT
    }
  },
  computed: {
    ...mapState('master', ['prefectures', 'prefectureOther', 'jobs']),
    corporateFgText() {
      return this.$store.getters['pages/users/my/user/corporateFgText']
    },
    sexText() {
      return this.$store.getters['pages/users/my/user/sexText']
    },
    isOtherLocation() {
      return this.attributes.user.profile.prefectureId === this.prefectureOther
    }
  },
  watch: {
    isOtherLocation() {
      if (!this.isOtherLocation) {
        this.attributes.user.profile.locationText = null
      }
    }
  },
  methods: {
    ...mapActions('pages/users/my/user', ['updateProfile', 'updateJobs', 'fetchMyUser']),
    async update() {
      if (!this.doValidate(false)) {
        // TODO 本当はflashではなくエラー時にスクロール処理を入れたい
        // 既存の仕組みはウィンドウ単位で動くため使えず、新規処理を書く必要がある
        this.$notify({
          group: 'flash',
          type: 'error',
          title: 'エラーがあります'
        })
        return
      }

      this.isLoading = true
      await this.updateProfile({
        profile: this.attributes.user.profile
      })
      await this.updateJobs({
        jobIds: this.attributes.userJobs
      })
      await this.fetchMyUser()
      this.isLoading = false
      this.doNotifySuccess('プロフィール基本情報を更新しました')
      this.$emit('close')
    }
  },
  created() {
    // NOTE: gRPCの仕様でnullが0に変換されているため、0値はnull扱い
    if (this.attributes.user.profile.prefectureId === 0)
      this.attributes.user.profile.prefectureId = null
  },
  validations() {
    const rules = {
      attributes: {
        user: {
          profile: {
            occupation: {
              required,
              maxLength: maxLength(MAX_OCCUPATION),
              emoji,
              char4Byte
            },
            locationText: {
              maxLength: maxLength(MAX_LOCATION_TEXT),
              emoji,
              char4Byte
            }
          }
        },
        userJobs: {
          required,
          maxArrayLength: maxArrayLength(MAX_USER_JOBS)
        }
      }
    }
    if (this.isOtherLocation) {
      rules.attributes.user.profile.locationText['required'] = required
    }
    return rules
  }
}
</script>
<style lang="scss" scoped>
.field:not(:last-child) {
  margin-bottom: 1.75rem;
}
.field {
  &.c-field {
    &_location {
      margin-bottom: 10px;
    }
  }
}
/deep/ .c-modal {
  width: 570px;
}
.c-editor {
  &_link {
    margin-top: 0.5em;
  }
  &_userIcon {
  }
  &_businessFormat {
  }
  &_businessFormatLink {
    margin-left: 1em;
  }
  &_occupation {
    width: 300px;
  }
  &_age {
  }
  &_sex {
  }
  &_location {
  }
  &_locationText {
    width: 300px;
  }
  &_job {
  }
  &_jobNotes {
  }
  &_theme {
  }
}
.c-formButtons {
  display: flex;

  justify-content: flex-end;
  .button + .button {
    margin-left: 10px;
  }
}

@media (max-width: $breakpoint-m) {
  /deep/ .c-modal {
    width: 100%;
  }
}

@media (max-width: $breakpoint-s) {
  .c-editor {
    &_occupation {
      width: 100%;
    }
    &_locationText {
      width: 100%;
    }
  }
}
</style>
